import React, { useState } from "react"

export function HateTheHate() {
  /* --- COLOR PALETTE & THEME --- */
  const colors = {
    darkGreen: "#013220",
    darkerGreen: "#002417", // For deeper contrast
    gold: "#D4AF37",
    textLight: "#ffffff",
    textDark: "#2e2e2e",
    backgroundMain: "#F3F3ED", // Less stark than pure white
    gradientHero: "linear-gradient(135deg, #013220, #046307)", // Hero background
  }

  /* --- KEYFRAME ANIMATIONS (inline) --- */
  const fadeInKeyframes = `
    @keyframes fadeSlideIn {
      0% {
        opacity: 0;
        transform: translateY(20px);
      }
      100% {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `

  /* --- HERO SECTION STYLES --- */
  const heroStyle: React.CSSProperties = {
    position: "relative",
    background: colors.gradientHero,
    height: "480px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    color: colors.textLight,
    overflow: "hidden",
  }

  const heroContentStyle = {
    zIndex: 2,
    maxWidth: "800px",
    margin: "0 auto",
    padding: "0 20px",
  }

  const heroTitleStyle = {
    fontSize: "3.2rem",
    marginBottom: "20px",
    fontWeight: "bold",
    color: colors.gold,
    textShadow: "2px 2px 6px rgba(0,0,0,0.3)",
  }

  const heroSubtitleStyle = {
    fontSize: "1.4rem",
    fontWeight: "500",
    textShadow: "1px 1px 5px rgba(0,0,0,0.2)",
  }

  // Wave divider for hero bottom
  const waveDividerStyle: React.CSSProperties = {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: "90px",
    fill: colors.backgroundMain,
    zIndex: 1,
  }

  /* --- MAIN CONTAINER --- */
  const containerStyle = {
    width: "90%",
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "40px 20px",
    backgroundColor: colors.backgroundMain,
  }

  /* --- SECTION CARD WRAPPER --- */
  const sectionCardStyle = {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
    padding: "30px",
    marginBottom: "50px",
  }

  const sectionHeadingStyle: React.CSSProperties = {
    marginBottom: "20px",
    fontSize: "2rem",
    color: colors.darkGreen,
    fontWeight: "600",
    textAlign: "center",
  }

  const paragraphStyle = {
    marginBottom: "16px",
    lineHeight: 1.7,
    color: colors.textDark,
  }

  /* --- CTA STYLES --- */
  const ctaSectionStyle: React.CSSProperties = {
    ...sectionCardStyle,
    textAlign: "center",
  }

  const ctaCardsWrapperStyle = {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "20px",
    marginTop: "20px",
  }

  const ctaCardStyle = {
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
    padding: "20px",
    transition: "transform 0.2s ease, box-shadow 0.2s",
  }

  /* --- COLLAPSIBLE STYLES --- */
  const collapsibleSectionStyle = {
    ...sectionCardStyle,
    backgroundColor: "#FFFDF5",
  }

  const collapsibleContentStyle = {
    marginTop: "20px",
    animation: "fadeSlideIn 0.4s ease forwards",
  }

  const collapsibleButtonStyle = {
    backgroundColor: colors.gold,
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "10px 20px",
    cursor: "pointer",
    fontWeight: "600",
    boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
  }

  /* --- SOCIAL MEDIA SECTION --- */
  const socialLinkStyle = {
    margin: "0 15px",
    color: colors.gold,
    fontWeight: "600",
    textDecoration: "none",
  }

  /* --- FOOTER DIVIDER & FOOTER --- */
  // Wave divider for top of the footer
  const waveDividerFooterStyle = {
    width: "100%",
    height: "80px",
    fill: colors.gradientHero, // Dark wave to blend with footer background
  }

  const footerStyle: React.CSSProperties = {
    textAlign: "center",
    padding: "40px 20px",
    background: `linear-gradient(135deg, ${colors.darkerGreen}, ${colors.darkGreen})`,
    color: "#ffffff",
  }

  const hashtagsStyle = {
    marginTop: "10px",
    color: "#FFD",
  }

  /* --- MEDIA QUERIES + KEYFRAMES --- */
  const mediaQueries = `
    ${fadeInKeyframes} /* inline keyframes animation */

    @media (min-width: 768px) {
      .cta-cards {
        grid-template-columns: repeat(3, 1fr);
      }
    }
  `

  // ---------------------------------------------------
  // ---------- STATE & COLLAPIBLE LOGIC ---------------
  // ---------------------------------------------------
  const [isCollapsed, setIsCollapsed] = useState(true)

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => !prev)
  }

  return (
    <div
      style={{
        fontFamily: "Poppins, sans-serif",
        backgroundColor: colors.backgroundMain,
      }}
    >
      {/* Inline Styles & Keyframes */}
      <style>{mediaQueries}</style>

      {/* HERO SECTION */}
      <div style={heroStyle}>
        <div style={heroContentStyle}>
          <h1 style={heroTitleStyle}>Join Our Weddinng Celebration</h1>
          <p style={heroSubtitleStyle}>
            February 23, 2025 | Fighting Hate with Kindness
          </p>
          <p
            style={{
              fontSize: "1.1rem",
              marginTop: "10px",
              color: colors.textLight,
            }}
          >
            Together, let’s stand against antisemitism and all forms of hate.
          </p>
        </div>
        {/* Wave Divider (SVG) */}
        <svg
          style={waveDividerStyle}
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
        >
          <path d="M0,288L48,272C96,256,192,224,288,186.7C384,149,480,107,576,112C672,117,768,171,864,192C960,213,1056,203,1152,197.3C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" />
        </svg>
      </div>

      {/* MAIN CONTAINER */}
      <div style={containerStyle}>
        {/* EVENT MESSAGE */}
        <section style={sectionCardStyle}>
          <h2 style={sectionHeadingStyle}>A Call to Action</h2>
          <p style={paragraphStyle}>
            On <strong>February 23, 2025</strong>, we’re celebrating a special
            day by turning it into a force for good. We invite all companies,
            celebrities, and individuals to join us in standing against
            antisemitism and hate. Instead of gifts or traditional shout-outs,
            please consider doing something meaningful that truly makes the
            world a better place.
          </p>
          <p style={paragraphStyle}>
            Together, we can counter hate with kindness—brightening the world
            one act at a time.
          </p>
          <p style={paragraphStyle}>
            Token actions are good, but larger, lasting efforts can transform
            communities for years to come!
          </p>
        </section>

        {/* CALL TO ACTION / RSVP SECTION */}
        <section style={ctaSectionStyle}>
          <h2 style={sectionHeadingStyle}>How to Join Our Cause</h2>
          <div className="cta-cards" style={ctaCardsWrapperStyle}>
            {/* Card 1 */}
            <div
              className="cta-card"
              style={ctaCardStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)"
                e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.15)"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)"
                e.currentTarget.style.boxShadow = "0 3px 10px rgba(0,0,0,0.08)"
              }}
            >
              <h3
                style={{
                  marginBottom: "10px",
                  fontSize: "1.4rem",
                  color: colors.darkGreen,
                  fontWeight: "600",
                }}
              >
                <span role="img" aria-label="check">
                  ✅
                </span>{" "}
                Commit an Act of Kindness
              </h3>
              <p style={{ marginBottom: "15px", lineHeight: 1.6 }}>
                Pick any idea below, or craft your own. The possibilities for
                goodness are endless.
              </p>
            </div>

            {/* Card 2 */}
            <div
              className="cta-card"
              style={ctaCardStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)"
                e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.15)"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)"
                e.currentTarget.style.boxShadow = "0 3px 10px rgba(0,0,0,0.08)"
              }}
            >
              <h3
                style={{
                  marginBottom: "10px",
                  fontSize: "1.4rem",
                  color: colors.darkGreen,
                  fontWeight: "600",
                }}
              >
                <span role="img" aria-label="check">
                  ✅
                </span>{" "}
                Share Your Story
              </h3>
              <p style={{ marginBottom: "15px", lineHeight: 1.6 }}>
                Post about it and tag <strong>@hi_tech_guy_hello_world</strong>{" "}
                to inspire others.
              </p>
            </div>

            {/* Card 3 */}
            <div
              className="cta-card"
              style={ctaCardStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)"
                e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.15)"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)"
                e.currentTarget.style.boxShadow = "0 3px 10px rgba(0,0,0,0.08)"
              }}
            >
              <h3
                style={{
                  marginBottom: "10px",
                  fontSize: "1.4rem",
                  color: colors.darkGreen,
                  fontWeight: "600",
                }}
              >
                <span role="img" aria-label="check">
                  ✅
                </span>{" "}
                Keep It Going
              </h3>
              <p style={{ marginBottom: "15px", lineHeight: 1.6 }}>
                Tag more brands, friends, or celebrities to spread the
                kindness—let’s create a ripple effect!
              </p>
            </div>
          </div>
          <p style={{ marginTop: "20px", fontStyle: "italic" }}>
            Our celebration happens on one day, but your positive impact can
            continue for a lifetime!
          </p>
        </section>

        {/* COLLAPSIBLE SECTION (IDEAS + ORGS) */}
        <section style={collapsibleSectionStyle}>
          <h2 style={sectionHeadingStyle}>
            Additional Ways to Make a Difference
          </h2>
          <p style={paragraphStyle}>
            Looking for extra inspiration? Check out these ideas (from small to
            large) and organizations to support. The sky’s the limit!
          </p>
          <div style={{ textAlign: "center" }}>
            <button style={collapsibleButtonStyle} onClick={toggleCollapsed}>
              {isCollapsed ? "Show More" : "Show Less"}
            </button>
          </div>

          {/* Collapsed/Expanded Content */}
          {!isCollapsed && (
            <div style={collapsibleContentStyle}>
              {/* IDEAS */}
              <div style={{ marginTop: "30px" }}>
                <h3
                  style={{
                    color: colors.darkGreen,
                    marginBottom: "10px",
                    textAlign: "center",
                  }}
                >
                  Idea Registry
                </h3>
                <h4 style={{ marginTop: "20px", color: colors.darkGreen }}>
                  Small Ideas
                </h4>
                <ul>
                  <li>Pay for someone’s coffee and leave a kind note.</li>
                  <li>
                    Offer free e-books or discount codes for content that can
                    help others.
                  </li>
                  <li>
                    Encourage employees/followers to share personal kindness
                    stories.
                  </li>
                </ul>

                <h4 style={{ marginTop: "20px", color: colors.darkGreen }}>
                  Medium Ideas
                </h4>
                <ul>
                  <li>
                    Make swag that raises awareness for anti-semitism and/or
                    hate.
                  </li>
                  <li>
                    Volunteer your staff’s time at a local shelter or community
                    center.
                  </li>
                  <li>
                    Sponsor an anti-hate online forum or panel discussion.
                  </li>
                </ul>

                <h4 style={{ marginTop: "20px", color: colors.darkGreen }}>
                  Large Ideas
                </h4>
                <ul>
                  <li>
                    Organize or sponsor an <strong>anti-hate hackathon</strong>
                    to develop innovative solutions to combat online harassment.
                  </li>
                  <li>
                    Launch a program to bring together disperate communities.
                  </li>
                  <li>
                    Partner with organizatins to create anti-hate initiatives
                    with their guidance.
                  </li>
                </ul>
              </div>

              {/* ORGANIZATIONS */}
              <div style={{ marginTop: "40px" }}>
                <h3
                  style={{
                    color: colors.darkGreen,
                    marginBottom: "10px",
                    textAlign: "center",
                  }}
                >
                  Organizations to Support
                </h3>
                <ul>
                  <li>
                    <strong>Anti-Defamation League (ADL):</strong> Fights
                    antisemitism & bigotry worldwide through advocacy and
                    education.
                  </li>
                  <li>
                    <strong>Simon Wiesenthal Center:</strong> Global institution
                    confronting hate through research, advocacy, and local
                    action.
                  </li>
                  <li>
                    There are plenty of others! Feel free to suggest we
                    highlight other orgs!
                  </li>
                </ul>
              </div>
            </div>
          )}
        </section>

        {/* FINAL NOTE */}
        <section style={sectionCardStyle}>
          <p style={paragraphStyle}>
            <strong>If you’d like to send us a wedding gift</strong>, we do
            welcome non-perishable items. But what we’d love even more is for
            you to keep this kindness alive—let’s not let hate define our world.
          </p>
          <p style={paragraphStyle}>
            Love and kindness are stronger. Thank you for being part of this
            special day and for helping us stand united against antisemitism and
            hate.
          </p>
          <p
            style={{
              marginTop: "30px",
              textAlign: "center",
              color: colors.darkGreen,
            }}
          >
            <strong>
              With gratitude,
              <br />
              Jason &amp; Zelda
            </strong>
          </p>
        </section>

        {/* SOCIAL MEDIA LINKS */}
        <section style={sectionCardStyle}>
          <h2 style={sectionHeadingStyle}>Stay Connected</h2>
          <p style={paragraphStyle}>
            Share your acts of kindness and help us amplify the good in the
            world! Tag us on social:
          </p>
          <div style={{ textAlign: "center", marginBottom: "10px" }}>
            <a
              href="https://instagram.com/hi_tech_guy_hello_world"
              target="_blank"
              rel="noreferrer"
              style={socialLinkStyle}
            >
              Instagram
            </a>
            {/* <a
              href="https://twitter.com/hi_tech_guy_hello_world"
              target="_blank"
              rel="noreferrer"
              style={socialLinkStyle}
            >
              Twitter
            </a>
            <a
              href="https://facebook.com/hi_tech_guy_hello_world"
              target="_blank"
              rel="noreferrer"
              style={socialLinkStyle}
            >
              Facebook
            </a> */}
          </div>
          {/* <p
            style={{
              fontStyle: "italic",
              marginTop: "10px",
              textAlign: "center",
            }}
          >
            Tag us: <strong>@hi_tech_guy_hello_world</strong>
          </p> */}
        </section>
      </div>

      {/* WAVE DIVIDER BEFORE FOOTER */}
      <svg
        style={waveDividerFooterStyle}
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
      >
        <path d="M0,64L48,90.7C96,117,192,171,288,181.3C384,192,480,160,576,138.7C672,117,768,107,864,133.3C960,160,1056,224,1152,224C1248,224,1344,160,1392,128L1440,96L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z" />
      </svg>

      {/* FOOTER */}
      <footer style={footerStyle}>
        <p style={{ marginBottom: "10px" }}>
          Thank you for standing with us against hate!
        </p>
        <div style={hashtagsStyle}>
          <span>#YoureInvited </span>
          <span>#KindnessForOurWedding </span>
          <span>#HateTheHate </span>
          <span>#LoveOverHate </span>
          <span>#MakeTheWorldBrighter </span>
          <span>#StrongerTogether</span>
        </div>
      </footer>
    </div>
  )
}
